import { z } from 'zod';
export const ProgressCompletionSchema = z.object({
    basis: z.literal('current_status'),
    state: z.enum(['empty', 'withdrawn', 'incomplete', 'complete']),
    unresolvedCount: z.number().int().nonnegative(),
    blockedCount: z.number().int().nonnegative(),
    doneBlockedCount: z.number().int().nonnegative(),
});
/**
 * Operational completion is separate from percentage and is not QA acceptance.
 * Include archived work: archival neither delivers nor removes an obligation.
 * This read never reconciles the manually maintained commitment status.
 */
export function progressWithCompletion(tickets) {
    const live = tickets.filter((t) => countsTowardProgress(t.status));
    const unresolvedCount = live.filter((t) => t.status !== 'done' || t.blocked).length;
    return {
        ...progressRollup(tickets.map((t) => t.status)),
        completion: {
            basis: 'current_status',
            state: tickets.length === 0 ? 'empty' : live.length === 0 ? 'withdrawn' : unresolvedCount ? 'incomplete' : 'complete',
            unresolvedCount,
            blockedCount: live.filter((t) => t.blocked).length,
            doneBlockedCount: live.filter((t) => t.status === 'done' && t.blocked).length,
        },
    };
}
/**
 * Onboarding progress weights — how much a ticket in each status counts toward
 * a promise (and, rolled up, a customer) being delivered. Progress is the MEAN
 * of the promise tickets' weights, so four tickets all in `dev_done` read 60%
 * rather than the 0% a done-only count gave.
 *
 * The weights follow the workflow lane (apps/api/src/lib/workflow.ts). From
 * `dev_done` on, a ticket's status mirrors where the code actually is (merged
 * to develop / uat / main), so this is a "distance to delivered" measure, not
 * a guess. Only `done` is 100 — a bar at 100% means every ticket has that status.
 * Consult completion separately: blocked done work is still unresolved.
 *
 * Retired statuses map to their live equivalent. Cancelled/rejected tickets
 * are not weighted at all — they leave the population (PROGRESS_EXCLUDED).
 */
export const STATUS_PROGRESS_WEIGHT = {
    draft: 0,
    submitted: 0,
    triage: 0,
    backlog: 0,
    design_needed: 0,
    acceptance_criteria_needed: 0,
    estimate_needed: 0,
    needs_approval: 0,
    ready_for_qa: 60,
    approved: 10,
    approved_for_agent: 10,
    in_progress: 30,
    agent_in_progress: 30,
    internal_waiting: 30,
    dev_done: 60,
    qa: 70,
    ready_for_uat: 75,
    uat: 90,
    staging: 95,
    done: 100,
    cancelled: 0,
    rejected: 0,
};
/**
 * Statuses that drop out of the progress population altogether: the work was
 * withdrawn, so it is neither delivered nor outstanding. A promise with one
 * done and one cancelled ticket reads 100% (1 of 1), not 50%.
 */
export const PROGRESS_EXCLUDED = new Set(['cancelled', 'rejected']);
/** True when a ticket in this status is part of the progress population. */
export function countsTowardProgress(status) {
    return !PROGRESS_EXCLUDED.has(status);
}
/** Weight for a status. Unknown strings weigh 0 rather than throwing. */
export function progressWeight(status) {
    return STATUS_PROGRESS_WEIGHT[status] ?? 0;
}
export function progressRollup(statuses) {
    const live = statuses.filter(countsTowardProgress);
    let sum = 0;
    let done = 0;
    for (const s of live) {
        sum += progressWeight(s);
        if (s === 'done')
            done += 1;
    }
    return {
        ticketCount: live.length,
        doneTicketCount: done,
        progressPct: live.length === 0 ? 0 : Math.round(sum / live.length),
    };
}
/** Whole-number percent (0–100) for a set of ticket statuses; see progressRollup. */
export function progressPct(statuses) {
    return progressRollup(statuses).progressPct;
}
/** The live lane in order, for legends/tooltips (retired + zero-weight statuses omitted). */
export const PROGRESS_LEGEND = ['approved', 'in_progress', 'dev_done', 'qa', 'ready_for_uat', 'uat', 'staging', 'done'].map((status) => ({ status, weight: STATUS_PROGRESS_WEIGHT[status] }));
//# sourceMappingURL=progress.js.map